import { OrderDirection, PageSize } from "enums/Common";
import { useCallback, useState } from "react";

import ButtonCreateOrder from "components/Buttons/ButtonCreateOrder/ButtonCreateOrder";
import DebouncedSearchField from "components/DebouncedSearchField/DebouncedSearchField";
import { IOrderFilters } from "interfaces/Order";
import { OrderCancelType, OrderFilterParams } from "enums/Order";
import Dropdown from "components/Dropdowns/Dropdown/Dropdown";
import { IDropdownOption } from "interfaces/Common";
import OrdersTable from "components/Tables/OrdersTable/OrdersTable";
import PageTitleDescription from "components/PageTitleDescription/PageTitleDescription";
import { useSearchParams } from "react-router-dom";

const ANY_STATUS_VALUE = -2; // avoid conflict with MenuOption.Unselected (-1)

// filters on order.cancel_type, so the cancelled / void states can be found
const cancelTypeOptions: IDropdownOption<number>[] = [
  { value: ANY_STATUS_VALUE, label: "Any" },
  { value: OrderCancelType.VoidRequested, label: "Void Requested" },
  { value: OrderCancelType.Voided, label: "Voided" },
  { value: OrderCancelType.Cancelled, label: "Canceled" },
];

const OrdersDashboard = () => {
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState<IOrderFilters>({
    _total_count: 1,
    page_size: PageSize.Default,
    page: 1,
    _order_by:
      searchParams.get("_order_by") || `order_num ${OrderDirection.Desc}`,
    [OrderFilterParams.OrderOrPatientName]:
      searchParams.get(OrderFilterParams.OrderOrPatientName) || undefined,
    id: searchParams.get("id") || undefined,
    cancelType: Number(searchParams.get("cancel_type")) || undefined,
  });

  const onPageIncrement = useCallback(
    () => setFilters((prev) => ({ ...prev, page: prev.page + 1 })),
    [setFilters]
  );

  return (
    <div className="h-full flex flex-col gap-5">
      <div className="flex flex-col gap-4 pr-5 xl:pr-0">
        <div className="flex flex-col gap-4 justify-between sm:flex-row">
          <PageTitleDescription
            className="w-full xl:w-fit"
            title="RKM Orders"
            description="Preview or create orders for remote kinematic monitoring."
          />

          <ButtonCreateOrder
            className="shrink-0 self-center"
            onCompleted={() => setFilters((prev) => ({ ...prev, page: 1, _refresh: Date.now() }))}
          />
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <DebouncedSearchField
            placeholder="Search by Patient Name or Order ID"
            value={filters[OrderFilterParams.OrderOrPatientName]}
            onChange={(value) =>
              setFilters((prev) => ({
                ...prev,
                page: 1,
                [OrderFilterParams.OrderOrPatientName]: value,
              }))
            }
          />

          <div className="flex items-center gap-3">
            <span className="text-cloudBurst font-medium">Status</span>
            <div className="min-w-[220px]">
              <Dropdown
                hideClearButton
                options={cancelTypeOptions}
                selected={filters.cancelType ?? ANY_STATUS_VALUE}
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    page: 1,
                    cancelType:
                      value === ANY_STATUS_VALUE || value === undefined
                        ? undefined
                        : (value as OrderCancelType),
                  }))
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grow h-0">
        <OrdersTable
          showPatientColumns
          filters={filters}
          onPageIncrement={onPageIncrement}
          onSortChange={(value) =>
            setFilters((prev) => ({ ...prev, page: 1, _order_by: value }))
          }
          refreshData={() => setFilters((prev) => ({ ...prev, page: 1, _refresh: Date.now() }))}
        />
      </div>
    </div>
  );
};

export default OrdersDashboard;
